import React, { FC, useCallback, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { TreeItemType, TreeMenu } from 'src/presentations/components/molecules/treeMenu';
import { BilledEntityItem } from 'src/use-cases/settings/useBilledEntities';

interface BilledEntitiesMenuProps {
  billedEntities: BilledEntityItem[];
  onClose: () => void;
  onApply?: (selectedEntityIds: string[]) => void;
  isVisible?: boolean;
  onToggle?: () => void;
  toggleButtonRef?: React.RefObject<HTMLButtonElement>;
  configOverrides?: Partial<{
    width: string;
    height: string;
    maxHeight: string;
  }>;
}

export const BilledEntitiesMenu: FC<BilledEntitiesMenuProps> = ({
  billedEntities,
  onClose,
  onApply,
  onToggle,
  isVisible = false,
  toggleButtonRef,
  configOverrides,
}): JSX.Element => {
  const { setValue, watch } = useFormContext();
  const selectedBilledEntityId = watch('billedEntityId') || '';

  // Helper function to recursively transform BilledEntityItem to TreeItem
  const transformToTreeItem = useCallback(
    (entity: BilledEntityItem): TreeItemType<string> => ({
      id: entity.id.toString(),
      label: entity.code,
      value: entity.id.toString(),
      selected: selectedBilledEntityId === entity.id.toString(),
      subItems: entity.subEntities?.map((subEntity) => transformToTreeItem(subEntity)),
      metadata: {
        statusColor: null,
      },
    }),
    [selectedBilledEntityId]
  );

  // Process billed entities into tree structure
  const treeItems = useMemo((): TreeItemType<string>[] => {
    if (!billedEntities || billedEntities.length === 0) return [];

    // Transform billed entities to tree items
    const entityItems = billedEntities.map((entity) => transformToTreeItem(entity));

    return entityItems;
  }, [billedEntities, transformToTreeItem]);

  const handleApply = useCallback(
    (selectedValues: string[]) => {
      // For single select, get the first selected value
      const selectedEntityId = selectedValues[0] || '';

      setValue('billedEntityId', selectedEntityId);

      if (onApply) {
        onApply([selectedEntityId]);
      }
    },
    [setValue, onApply]
  );

  // Tree menu configuration
  const config = {
    title: 'Billed Entities',
    searchPlaceholder: 'Search',
    width: '380px',
    height: '600px',
    maxHeight: '80vh',
    showSearch: true,
    showSelectAll: false,
    showClear: false,
    ...configOverrides,
  };

  return (
    <TreeMenu
      items={treeItems}
      config={config}
      isVisible={isVisible}
      isLoading={false}
      selectedValues={selectedBilledEntityId ? [selectedBilledEntityId] : []}
      onClose={onClose}
      onApply={handleApply}
      onToggle={onToggle}
      toggleButtonRef={toggleButtonRef}
      isMultiSelect={false}
    />
  );
};
